import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import SmartImage from "@/components/SmartImage";
import { SocialIcons } from "@/components/Social";
import { CalendarHeart, Ticket, PartyPopper, Heart, Users, Sparkles, HeartHandshake, Dice5 } from "lucide-react";

const HERO_CACHE_KEY = "oa_home_hero_v1";

const resolveHeroUrl = (heroImg) => {
  if (!heroImg) return null;
  return heroImg.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${heroImg}` : heroImg;
};

// Broader event lineup — dating is one category among many. Order matters: lead with variety, not dating.
const EXPERIENCES = [
  { key: "Special", icon: Sparkles, title: "Themed & seasonal", tag: "Signature nights", copy: "Holiday parties, vineyard evenings, rooftop socials, and the standout events we plan just for the season." },
  { key: "Friends", icon: Users, title: "Social nights", tag: "Group hangs & game nights", copy: "Girls' nights, game nights, paint-and-sips, and easy social hangs when you just want a fun night out." },
  { key: "Couples", icon: HeartHandshake, title: "Couples", tag: "Date nights, planned", copy: "Chef's tables, tastings, and curated date nights — the plan is already made, all you have to do is show up." },
  { key: "Singles", icon: Heart, title: "Singles & dating", tag: "Speed dating & mixers", copy: "Age-grouped speed dating and singles mixers when you're up for meeting someone new — no swiping required." },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [cms, setCms] = useState({});
  const [heroUrl, setHeroUrl] = useState(() => {
    try { return localStorage.getItem(HERO_CACHE_KEY) || null; }
    catch { return null; }
  });

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data.slice(0, 3))).catch(err => console.warn("home events fetch failed", err));
    api.get("/cms/content").then(r => {
      setCms(r.data);
      const resolved = resolveHeroUrl(r.data["home.hero.image"]);
      if (resolved) {
        setHeroUrl(resolved);
        try { localStorage.setItem(HERO_CACHE_KEY, resolved); }
        catch { /* localStorage unavailable — ignore */ }
      }
    }).catch(err => console.warn("home cms fetch failed", err));
  }, []);

  // Section visibility — any CMS key `home.sections.<name>.visible` defaults to true.
  // Setting it to "false" from Admin → Content hides that section entirely.
  const sectionOn = (name) => cms[`home.sections.${name}.visible`] !== "false";

  // Section order — comma-separated list stored under `home.sections.order`. Any missing section
  // falls back to the canonical order so reordering never hides a section by omission.
  const DEFAULT_ORDER = ["upcoming", "experiences", "story", "connect", "manifesto"];
  const orderRaw = (cms["home.sections.order"] || "").trim();
  const orderList = orderRaw ? orderRaw.split(",").map(s => s.trim()).filter(Boolean) : DEFAULT_ORDER;
  const fullOrder = [...orderList, ...DEFAULT_ORDER.filter(s => !orderList.includes(s))];
  const sectionOrder = (name) => {
    const idx = fullOrder.indexOf(name);
    return idx === -1 ? 99 : idx;
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-neutral-900">
        {heroUrl && (
          <SmartImage
            src={heroUrl}
            alt=""
            className="absolute inset-0 w-full h-full"
            fetchPriority="high"
          />
        )}
        <div className="absolute inset-0" style={{background: "linear-gradient(180deg, rgba(20,15,10,0.15) 0%, rgba(20,15,10,0.35) 55%, rgba(20,15,10,0.7) 100%)"}} />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-24 md:pb-32">
          <div className="max-w-3xl text-cream animate-fade-up">
            <div className="text-eyebrow text-[hsl(46_65%_70%)]">{cms["home.hero.eyebrow"] || "Events · Experiences · Celebrations"}</div>
            <h1 className="font-heading text-5xl md:text-7xl leading-[1.02] tracking-tight text-white mt-6" data-testid="hero-headline">
              {cms["home.hero.headline"] || "Experiences worth showing up for."}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl">
              {cms["home.hero.subheadline"] || "Offline & Available is an event brand for adults who like to get out of the house. Speed dating, themed parties, couples nights, game nights, holiday events, and the kind of social experiences you'll actually want to post about."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/events" data-testid="hero-browse-events">
                <Button className="rounded-full bg-white text-black hover:bg-cream btn-pill">See upcoming events</Button>
              </Link>
              <Link to="/how-it-works" data-testid="hero-what-we-do">
                <Button variant="outline" className="rounded-full bg-transparent text-white border-white/70 hover:bg-white/10 btn-pill">What we do</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* All configurable sections live in a flex container so admin-defined ordering (CSS `order`)
          takes effect without touching JSX. Hero is intentionally fixed on top. */}
      <div className="flex flex-col">

      {/* Upcoming events — moved up so the first thing visitors see is what's on the calendar */}
      {sectionOn("upcoming") && (
      <section className="max-w-7xl mx-auto px-6 pt-24 pb-8" id="upcoming" style={{ order: sectionOrder("upcoming") }}>
        <div className="flex items-end justify-between mb-12">
          <div className="max-w-2xl">
            <div className="text-eyebrow">Upcoming events</div>
            <h2 className="font-heading text-4xl md:text-5xl mt-4">On the calendar</h2>
            <p className="text-neutral-600 mt-4 leading-relaxed">Grab a ticket and show up. Every event is planned, hosted, and produced by us.</p>
          </div>
          <Link to="/events" className="hidden md:inline text-sm underline underline-offset-4" data-testid="home-view-all-events">See full schedule →</Link>
        </div>
        {events.length === 0 ? (
          <div className="card-elegant p-12 text-center text-neutral-600">Fresh events dropping soon — <Link to="/events" className="underline">check the full schedule</Link> or follow along on social for the next release.</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {events.map(e => <EventCard key={e.event_id} event={e} />)}
          </div>
        )}
        <div className="md:hidden mt-8 text-center">
          <Link to="/events" className="text-sm underline underline-offset-4">See full schedule →</Link>
        </div>
      </section>
      )}

      {/* What we do / Explore by experience */}
      {sectionOn("experiences") && (
      <section className="bg-blush/30 py-24 mt-16" id="experiences" style={{ order: sectionOrder("experiences") }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <div className="text-eyebrow">{cms["home.experiences.eyebrow"] || "What we do"}</div>
            <h2 className="font-heading text-4xl md:text-5xl mt-4">{cms["home.experiences.headline"] || "One brand. A whole lineup of ways to show up."}</h2>
            <p className="text-neutral-600 mt-6 leading-relaxed">{cms["home.experiences.subhead"] || "Every event is planned, hosted, and produced by us — from the venue to the vibe. Come solo, bring a friend, bring your person. There's a room for every mood."}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXPERIENCES.map(e => (
              <Link key={e.key} to={`/events?category=${e.key}`} className="card-elegant p-8 group cursor-pointer hover:-translate-y-1 transition-transform" data-testid={`exp-${e.key}`}>
                <e.icon className="gold" size={28} strokeWidth={1.5} />
                <div className="text-eyebrow mt-6">{e.tag}</div>
                <h3 className="font-heading text-3xl mt-2">{e.title}</h3>
                <p className="text-neutral-600 mt-4 leading-relaxed text-sm">{e.copy}</p>
                <div className="mt-6 text-sm underline underline-offset-4 group-hover:gold">Browse {e.title.toLowerCase()} →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* The experience — brand story */}
      {sectionOn("story") && (
      <section className="py-24" id="the-experience" style={{ order: sectionOrder("story") }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="text-eyebrow">The experience</div>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-tight">The night is the plan.</h2>
            <p className="text-neutral-600 mt-6 leading-relaxed">
              We pick the venues, we set the vibe, we run the room. You just get to enjoy it. Every Offline &amp; Available event is designed to feel effortless — a place to meet new people, catch up with friends, or make an ordinary Thursday feel like a real occasion.
            </p>
            <p className="text-neutral-600 mt-4 leading-relaxed">
              No swiping. No scripts. No awkward "so… what do you do?" networking. Just good rooms, good people, and events worth talking about the next morning.
            </p>
            <Link to="/how-it-works" className="inline-block mt-8" data-testid="home-experience-cta">
              <Button variant="outline" className="rounded-full btn-pill">Learn more →</Button>
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-6">
            <StatCard icon={CalendarHeart} big="20+" small="events every season" />
            <StatCard icon={PartyPopper} big="10+" small="event formats" />
            <StatCard icon={Ticket} big="1000s" small="tickets to good nights" />
            <StatCard icon={Dice5} big="Endless" small="reasons to leave the couch" />
          </div>
        </div>
      </section>
      )}

      {/* Stay Connected */}
      {sectionOn("connect") && (
      <section className="max-w-7xl mx-auto px-6 pb-24" id="stay-connected" style={{ order: sectionOrder("connect") }}>
        <div className="card-elegant p-10 md:p-16 relative overflow-hidden" data-testid="stay-connected-section">
          <div className="absolute inset-0 pointer-events-none opacity-30" style={{background: "radial-gradient(circle at top right, hsl(21 35% 70% / 0.4), transparent 55%), radial-gradient(circle at bottom left, hsl(46 65% 52% / 0.15), transparent 55%)"}} />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="text-eyebrow gold">Join the list</div>
              <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-tight">{cms["home.connect.headline"] || "Stay in the loop."}</h2>
              <p className="mt-6 text-neutral-700 leading-relaxed text-lg max-w-2xl">
                {cms["home.connect.body"] || "Follow along on Instagram, Facebook, and TikTok and keep an eye on our schedule — new events drop often, and the best ones fill fast."}
              </p>
              <div className="mt-8"><SocialIcons size={26} gap="gap-6" className="text-neutral-800" /></div>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <Link to="/events"><Button className="w-full rounded-full bg-black text-white hover:bg-neutral-800 btn-pill" data-testid="stay-connected-schedule">See event schedule</Button></Link>
              <Link to="/signup"><Button variant="outline" className="w-full rounded-full btn-pill" data-testid="stay-connected-signup">Create an account</Button></Link>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Manifesto */}
      {sectionOn("manifesto") && (
      <section className="bg-black text-cream py-24" style={{ order: sectionOrder("manifesto") }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-eyebrow text-[hsl(46_65%_70%)]">Our promise</div>
          <h2 className="font-heading text-4xl md:text-5xl mt-6 leading-tight">{cms["home.manifesto.headline"] || "Where the Wi-Fi ends, "}<em className="italic text-[hsl(46_65%_75%)]">the experience begins.</em></h2>
          <p className="mt-8 text-white/70 leading-relaxed text-lg">{cms["home.manifesto.body"] || "We throw the kind of events you used to hope someone else would host — real rooms, real people, and nights you'll actually remember."}</p>
          <Link to="/events" className="inline-block mt-10" data-testid="home-manifesto-cta">
            <Button className="rounded-full bg-[hsl(46_65%_52%)] hover:bg-[hsl(46_65%_45%)] text-black btn-pill">Browse events</Button>
          </Link>
        </div>
      </section>
      )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, big, small }) => (
  <div className="card-elegant p-6 md:p-8">
    <Icon className="gold" size={22} strokeWidth={1.5} />
    <div className="font-heading text-4xl md:text-5xl mt-4">{big}</div>
    <div className="text-eyebrow mt-2">{small}</div>
  </div>
);
